/**
 * Represents the category of tracking events
 */
type TrackingCategory =
  | 'job-market'
  | 'job-search'
  | 'user-profile'
  | 'navigation'
  | 'form-submission'
// Add more categories as needed

/**
 * Represents the category of site search events
 */
type SiteSearchCategory =
  | 'job-search'
  | 'legal-search'
  | 'advanced-training-search'
  | 'professional-event-search'

/**
 * Configuration for a tracking event
 */
type EventTrackingConfig = {
  /** The category of the event */
  category: TrackingCategory
  /** The specific action being tracked */
  action: string
  /** Optional name/label for the event */
  name?: string
  /** Optional numeric value associated with the event */
  value?: number
}

/**
 * Configuration for a tracking site search event
 */
type SiteSearchTrackingConfig = {
  /** The category of the event */
  category: SiteSearchCategory
  /** The specific action being tracked */
  keyword: string
  /** Optional name/label for the event */
  count?: number
}

/**
 * Type declaration for Matomo's tracking array
 */
declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    _paq: any[]
  }
}

/**
 * Creates a tracking function for Matomo events
 *
 * @param config - The configuration object for the tracking event
 * @returns A function that can be called to trigger the tracking
 * @throws Error if Matomo tracking is not initialized (_paq is undefined)
 *
 * @example
 * ```typescript
 * const trackJobApplication = createTrackingEvent({
 *   category: 'job-market',
 *   action: 'apply-now'
 * });
 *
 * // In your component:
 * <button @click="trackJobApplication()">Apply Now</button>
 * ```
 */
export function createTrackingEvent(config: EventTrackingConfig) {
  return () => {
    if (typeof window === 'undefined' || !window._paq) {
      console.error('Matomo tracking is not initialized')
    }
    window._paq?.push([
      'trackEvent',
      config.category,
      config.action,
      config.name || '',
      config.value || 0,
    ])
  }
}

/**
 * Utility function to create multiple tracking events at once
 *
 * @param configs - Object containing multiple tracking configurations
 * @returns Object with the same keys but tracking function values
 *
 * @example
 * ```typescript
 * const trackingEvents = createTrackingEvents({
 *   applicationSubmit: {
 *     category: 'job-market',
 *     action: 'apply-now'
 *   },
 *   profileView: {
 *     category: 'user-profile',
 *     action: 'view'
 *   }
 * });
 *
 * // In your component:
 * <button @click="trackingEvents.applicationSubmit()">Apply</button>
 * ```
 */
export function createTrackingEvents<T extends Record<string, EventTrackingConfig>>(configs: T): { [K in keyof T]: () => void } {
  return Object.entries(configs).reduce((acc, [key, config]) => {
    return {
      ...acc,
      [key]: createTrackingEvent(config),
    }
  }, {} as { [K in keyof T]: () => void })
}

/**
 * Creates a tracking function for Matomo Site Search events
 *
 * @param config - The configuration object for the tracking event
 * @returns A function that can be called to trigger the tracking
 * @throws Error if Matomo tracking is not initialized (_paq is undefined)
 *
 * @example
 * ```typescript
 * const trackJobApplication = createTrackingSiteSearchEvent({
 *   keyword: 'Sozialpädagog:in',
 *   category: 'job-search',
 * });
 *
 * // In your component:
 * <button @click="trackJobApplication()">Apply Now</button>
 * ```
 */
export function createTrackingSiteSearchEvent(config: SiteSearchTrackingConfig) {
  return () => {
    if (typeof window === 'undefined' || !window._paq) {
      console.error('Matomo tracking is not initialized')
    }

    window._paq?.push([
      'trackSiteSearch',
      config.keyword,
      config.category,
      config.count || 0,
    ])
  }
}
